import { motion, AnimatePresence } from "framer-motion";
import { SlidersHorizontal, X, Truck, Tag, LocateFixed, Loader2, Search, Sparkles, MapPin } from "lucide-react";
import i18n from "@/i18n";
import { getIcon } from "./iconMap";
import { GPS } from "@/constants/testIds";
import { TRAVEL_MODES, TRAVEL_ORDER, RADIUS_OPTIONS } from "@/lib/mapConstants";

// Filters bottom sheet — travel mode, radius, trucker & categories (extracted from GpsOverlay, Map Cleanup Stage 6).
export const FiltersSheet = ({ open, onClose, travelMode, changeMode, radius, setRadius, truckerMode, toggleTrucker, category, setCategory, categories }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        className="absolute inset-0 z-[1900] flex items-end sm:items-center justify-center bg-black/50"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          data-testid="filters-sheet"
          initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 40 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong rounded-t-3xl sm:rounded-3xl w-full sm:max-w-md p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] relative max-h-[88vh] flex flex-col"
        >
          <div className="flex items-center justify-between mb-4 shrink-0">
            <h2 className="font-heading text-lg font-bold flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-sky-400" /> Filters
            </h2>
            <button data-testid="filters-close" onClick={onClose} className="w-9 h-9 rounded-xl hover:bg-slate-700/60 flex items-center justify-center">
              <X className="w-4 h-4 text-slate-300" />
            </button>
          </div>

          <div className="overflow-y-auto no-scrollbar -mx-1 px-1">
            {/* Travel mode */}
            <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-2">How are you moving?</p>
            <div data-testid="travel-mode-selector" className="grid grid-cols-4 gap-2 mb-5">
              {TRAVEL_ORDER.map((m) => {
                const M = TRAVEL_MODES[m];
                const MIco = M.icon;
                const on = travelMode === m;
                return (
                  <button
                    key={m}
                    data-testid={`travel-mode-${m}`}
                    onClick={() => changeMode(m)}
                    className={`h-16 rounded-xl flex flex-col items-center justify-center gap-1 text-xs font-bold transition-colors ${on ? "bg-sky-500 text-slate-900" : "bg-slate-800/60 text-slate-300 hover:bg-slate-700/60"}`}
                  >
                    <MIco className="w-5 h-5" /> {M.label}
                  </button>
                );
              })}
            </div>

            {/* Radius */}
            <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-2">Scan radius</p>
            <div data-testid={GPS.radiusSelector} className="flex gap-1.5 mb-5">
              {RADIUS_OPTIONS.map((r) => (
                <button
                  key={r}
                  data-testid={`radius-${r}`}
                  onClick={() => setRadius(r)}
                  className={`flex-1 h-11 rounded-xl text-sm font-bold transition-colors ${radius === r ? "bg-sky-500 text-slate-900" : "bg-slate-800/60 text-slate-300 hover:bg-slate-700/60"}`}
                >
                  {r}mi
                </button>
              ))}
            </div>

            {/* Trucker mode toggle */}
            <button
              data-testid="trucker-mode-btn"
              onClick={toggleTrucker}
              className={`w-full h-14 rounded-xl px-4 flex items-center gap-3 mb-5 transition-colors ${truckerMode ? "bg-amber-500/20 border border-amber-400/40" : "bg-slate-800/60 hover:bg-slate-700/60"}`}
            >
              <Truck className={`w-5 h-5 ${truckerMode ? "text-amber-300" : "text-amber-400"}`} />
              <span className="flex-1 text-left">
                <span className="block font-heading font-bold text-sm">Trucker Mode</span>
                <span className="block text-[11px] text-slate-400">Truck stops, parking, weigh stations & more</span>
              </span>
              <span className={`shrink-0 w-12 h-7 rounded-full flex items-center px-0.5 transition-colors ${truckerMode ? "bg-amber-400 justify-end" : "bg-slate-700 justify-start"}`}>
                <span className="w-6 h-6 rounded-full bg-white shadow" />
              </span>
            </button>

            {/* Categories */}
            <p className="text-[11px] font-bold tracking-[0.18em] uppercase text-slate-400 mb-2">Category</p>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                data-testid="category-option-all"
                onClick={() => { setCategory(null); onClose(); }}
                className={`col-span-2 h-11 rounded-xl px-3 text-left text-sm font-semibold flex items-center gap-2.5 transition-colors ${!category ? "bg-sky-500/20 text-sky-300" : "bg-slate-800/50 hover:bg-slate-700/60"}`}
              >
                <Tag className="w-4 h-4 text-sky-400" /> All / Random
              </button>
              {categories.map((c) => {
                const Ico = getIcon(c.icon);
                const on = category === c.id;
                return (
                  <button
                    key={c.id}
                    data-testid={`category-option-${c.id}`}
                    onClick={() => { setCategory(c.id); onClose(); }}
                    className={`min-h-11 py-1.5 rounded-xl px-3 text-left text-sm font-medium flex items-center gap-2 transition-colors ${on ? "bg-sky-500/20 text-sky-300" : "bg-slate-800/50 hover:bg-slate-700/60"} ${c.trucker ? "ring-1 ring-amber-400/30" : ""}`}
                  >
                    <Ico className={`w-4 h-4 shrink-0 ${c.trucker ? "text-amber-400" : "text-slate-400"}`} />
                    <span className="line-clamp-2 leading-tight text-[13px]">{i18n.t(`categories.${c.id}`, { defaultValue: c.label })}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// Location search modal — GPS / city / craving search (extracted from GpsOverlay, Map Cleanup Stage 6).
export const LocationSearchModal = ({ open, onClose, travelMode, requestGeo, locQuery, setLocQuery, doGeoSearch, locSearching, searchAsDeals, locResults, setExplore }) => (
  <AnimatePresence>
    {open && (
      <motion.div
        className="absolute inset-0 z-[2000] flex items-start justify-center p-4 pt-20 sm:pt-24 bg-black/50"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong rounded-3xl w-full max-w-md p-6 relative"
        >
          <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-xl hover:bg-slate-700/60 flex items-center justify-center">
            <X className="w-5 h-5 text-slate-300" />
          </button>
          <h2 className="font-heading text-xl font-bold mb-1">{travelMode === "fly" || travelMode === "transit" ? "Where are you headed?" : "Set your location"}</h2>
          <p className="text-slate-400 text-sm mb-4">{travelMode === "fly" || travelMode === "transit" ? "Search your destination to preview the deals waiting there." : "Use GPS, search a city/address, or type what you're craving (\"tacos\", \"coffee\") to find it nearby."}</p>

          <button
            data-testid={GPS.useGpsBtn}
            onClick={requestGeo}
            className="w-full h-12 rounded-xl bg-sky-500 text-slate-900 font-heading font-bold flex items-center justify-center gap-2 hover:bg-sky-400 transition-colors mb-4"
          >
            <LocateFixed className="w-5 h-5" /> Use my GPS location
          </button>

          <div className="flex items-center gap-3 mb-3">
            <div className="h-px bg-slate-700/60 flex-1" />
            <span className="text-xs text-slate-500 uppercase tracking-wider">or search</span>
            <div className="h-px bg-slate-700/60 flex-1" />
          </div>

          <div className="flex gap-2 mb-3">
            <input
              data-testid={GPS.locationSearchInput}
              value={locQuery}
              onChange={(e) => setLocQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && doGeoSearch()}
              placeholder="City, address — or 'tacos', 'coffee'…"
              className="flex-1 h-12 rounded-xl bg-slate-800/70 border border-slate-700/60 px-3 text-sm outline-none focus:border-sky-400/60"
            />
            <button
              data-testid={GPS.locationSearchSubmit}
              onClick={doGeoSearch}
              disabled={locSearching}
              className="w-12 h-12 rounded-xl bg-slate-800/70 hover:bg-slate-700/70 flex items-center justify-center disabled:opacity-60"
            >
              {locSearching ? <Loader2 className="w-5 h-5 animate-spin text-sky-400" /> : <Search className="w-5 h-5 text-sky-400" />}
            </button>
          </div>

          {locQuery.trim() && (
            <button
              data-testid="search-as-deals"
              onClick={() => searchAsDeals()}
              className="w-full mb-3 h-11 rounded-xl bg-gradient-to-r from-sky-500/20 to-violet-500/20 border border-sky-400/40 text-sky-100 font-heading font-bold text-sm flex items-center justify-center gap-2 hover:from-sky-500/30 hover:to-violet-500/30 transition-colors"
            >
              <Sparkles className="w-4 h-4 text-sky-300" /> Find "{locQuery.trim()}" nearby — Ask AIR.POG
            </button>
          )}

          <div className="space-y-2 max-h-[40vh] overflow-y-auto no-scrollbar">
            {locResults.map((r) => (
              <button
                key={`${r.lat},${r.lng}`}
                data-testid={GPS.locationResult}
                onClick={() => setExplore(r.lat, r.lng, r.label)}
                className="w-full text-left bg-slate-800/50 hover:bg-slate-700/60 rounded-xl p-3 flex items-start gap-3 transition-colors"
              >
                <MapPin className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" />
                <span className="text-sm leading-snug">{r.label}</span>
              </button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);
